import React, { useEffect } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { Layout } from './components/Layout';
import { Toaster } from './components/ui/sonner';
import { initAnalytics, trackPageView } from './lib/analytics';

// Pages
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ConfirmEmail from './pages/ConfirmEmail';
import PrivateClass from './pages/PrivateClass';
import Reserver from './pages/Reserver';
import MesSeances from './pages/MesSeances';
import Abonnement from './pages/Abonnement';
import MandatSigne from './pages/MandatSigne';
import NosAbonnements from './pages/NosAbonnements';
import UniversPuro from './pages/UniversPuro';
import LegalPage from './pages/LegalPage';
import EspaceCoach from './pages/EspaceCoach';
import CoachActivation from './pages/CoachActivation';
import Profil from './pages/Profil';
import MaSante from './pages/MaSante';
import CoachDashboard from './pages/CoachDashboard';
import Notifications from './pages/Notifications';
import Checkin from './pages/Checkin';
import Contact from './pages/Contact';
import Planning from './pages/Planning';
import SondageHoraires from './pages/SondageHoraires';
import Inauguration from './pages/Inauguration';
import ClubPrivilege from './pages/ClubPrivilege';
import AwaitingAccess from './pages/AwaitingAccess';


// Protected Route Component
function ProtectedRoute({ children, requireCoach = false, requireAdmin = false, allowSinglePlan = false, allowNoPlan = false }) {
  const { user, loading, isCoach, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5EFE5]">
        <div className="spinner" />
      </div>
    );
  }

  if (!user || user === false) {
    return <Navigate to="/login" replace />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to={user.role === 'coach' ? '/espace-coach' : '/'} replace />;
  }

  if (requireCoach && !isCoach) {
    return <Navigate to="/" replace />;
  }

  // Bypass access guard for coach (admins always have full access)
  if (!isCoach) {
    const plan = user?.chosen_plan;
    const noPlan = !plan;
    const isSingle = plan === 'single';

    if (noPlan && !allowNoPlan) {
      return <AwaitingAccess reason="no-plan" />;
    }
    if (isSingle && !allowSinglePlan) {
      return <AwaitingAccess reason="single" />;
    }
  }

  return children;
}

// Guest Route Component (only for non-authenticated users)
function GuestRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5EFE5]">
        <div className="spinner" />
      </div>
    );
  }
  
  if (user && user !== false) {
    return <Navigate to={user.role === 'admin' ? '/coach' : user.role === 'coach' ? '/espace-coach' : '/'} replace />;
  }
  
  return children;
}

function AppRoutes() {
  const location = useLocation();
  useEffect(() => {
    // First mount: initialise analytics (also fires first page view)
    initAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    // Subsequent route changes
    trackPageView();
  }, [location.pathname]);
  return (
    <Layout>
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Home />} />
        <Route path="/reserver" element={<ProtectedRoute><Reserver /></ProtectedRoute>} />
        <Route path="/classes" element={<ProtectedRoute><Reserver /></ProtectedRoute>} />
        <Route path="/nos-abonnements" element={<NosAbonnements />} />
        <Route path="/univers" element={<UniversPuro />} />
        <Route path="/legal/:docType" element={<LegalPage />} />
        <Route path="/tarifs" element={<NosAbonnements />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/planning" element={<Planning />} />
        <Route path="/sondage" element={<ProtectedRoute><SondageHoraires /></ProtectedRoute>} />
        <Route path="/inauguration" element={<Inauguration />} />
        
        {/* Guest Routes */}
        <Route 
          path="/login" 
          element={
            <GuestRoute>
              <Login />
            </GuestRoute>
          } 
        />
        <Route 
          path="/register" 
          element={<Register />}
        />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/confirm" element={<ConfirmEmail />} />
        <Route path="/cours-prive" element={<PrivateClass />} />
        
        {/* Protected Routes - Client */}
        <Route 
          path="/mes-seances" 
          element={
            <ProtectedRoute>
              <MesSeances />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/my-bookings" 
          element={
            <ProtectedRoute>
              <MesSeances />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/abonnement" 
          element={
            <ProtectedRoute allowSinglePlan allowNoPlan>
              <Abonnement />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/abonnement/mandat-signe" 
          element={
            <ProtectedRoute allowSinglePlan allowNoPlan>
              <MandatSigne />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/profil" 
          element={
            <ProtectedRoute allowSinglePlan allowNoPlan>
              <Profil />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/club" 
          element={
            <ProtectedRoute>
              <ClubPrivilege />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/ma-sante" 
          element={
            <ProtectedRoute allowSinglePlan allowNoPlan>
              <MaSante />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/notifications" 
          element={
            <ProtectedRoute>
              <Notifications />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/checkin/:classId" 
          element={
            <ProtectedRoute>
              <Checkin />
            </ProtectedRoute>
          } 
        />
        
        {/* Admin Routes */}
        <Route 
          path="/coach" 
          element={
            <ProtectedRoute requireAdmin>
              <CoachDashboard />
            </ProtectedRoute>
          } 
        />
        <Route
          path="/espace-coach"
          element={
            <ProtectedRoute requireCoach>
              <EspaceCoach />
            </ProtectedRoute>
          }
        />
        <Route path="/coach-activation" element={<CoachActivation />} />
        
        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <AppRoutes />
          <Toaster 
            position="top-center"
            toastOptions={{
              style: {
                background: '#FFFFFF',
                border: '1px solid #E8E4DB',
                borderRadius: '1rem',
                fontFamily: 'Manrope, sans-serif',
              }
            }}
          />
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
